import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { updateGroceryOrderStatus } from './groceryApi';
import {
  onlineDisplayStatusToOrderStatusCode,
  resolveOrderStatusRaw,
} from './orderStatusApi';
import { parseDateSortValue, str } from './tableSortUtils';
import { attachOrderBillingFields } from './orderBillingUtils';

const ORDER_STATUS_FROM_CODE = {
  CAN: 'CANCELLED',
  DEL: 'DELIVERED',
  CMP: 'COMPLETED',
  PU: 'PICKED-UP',
  UND: 'UNDELIVERED',
};

/** Resolve order primary key from API row (snake_case, camelCase, or PascalCase). */
export function resolveOrderId(order) {
  if (!order || typeof order !== 'object') return null;
  const id =
    order.cateringOrderId ??
    order.catering_order_id ??
    order.Catering_Order_Id ??
    order.order_id ??
    order.orderId ??
    order.OrderId ??
    order.Order_Id ??
    order.grocery_order_id ??
    order.groceryOrderId ??
    order.orderNumber ??
    order.OrderNumber;
  if (id == null || id === '') return null;
  return id;
}

/** @alias resolveOrderId — courier / delivery-agent tables */
export function resolveDeliveryAgentOrderId(order) {
  return resolveOrderId(order);
}

export function resolvePartySize(order) {
  const n = Number(order?.partySize ?? order?.party_size);
  return Number.isFinite(n) && n >= 2 ? n : null;
}

/** Normalize order row from GET /restaurants/:id/orders or order-history. */
export function normalizeRestaurantOrder(order, { mapHistoryStatusFromCode = false } = {}) {
  if (!order || typeof order !== 'object') return order;

  const code = String(
    order.orderStatusCode ?? order.Order_Status_Code ?? order.order_status_code ?? ''
  )
    .trim()
    .toUpperCase();

  const statusFromApi =
    order.orderStatus ?? order.OrderStatus ?? order.order_status ?? '';
  const orderStatus =
    statusFromApi ||
    (mapHistoryStatusFromCode ? ORDER_STATUS_FROM_CODE[code] : '') ||
    '';

  const rawLineItems =
    order.orderLineItems ?? order.OrderLineItems ?? order.order_line_items ?? [];
  const lineItems = rawLineItems.map((li) => ({
    ...li,
    foodItemName:
      li.foodItemName ?? li.food_item_name ?? li.Food_Item_Name ?? li.itemName,
    foodQuantity:
      li.foodQuantity ?? li.food_quantity ?? li.Food_Quantity ?? li.quantity ?? 1,
    price: li.price ?? li.Price,
    orderLineId: li.orderLineId ?? li.order_line_id ?? li.Order_LineItem_Id,
    dailyItemId:
      li.dailyItemId ??
      li.daily_item_id ??
      li.Daily_Item_Id ??
      li.DailyItemId ??
      null,
    orderedAmount: li.orderedAmount ?? li.ordered_amount ?? null,
    servingUnit: li.servingUnit ?? li.serving_unit ?? null,
    displayQuantity: li.displayQuantity ?? li.display_quantity ?? null,
  }));
  const payment = order.payment ?? order.Payment;

  const orderId = resolveOrderId(order);
  const paymentStatus =
    order.paymentStatus ??
    order.payment_status ??
    order.PaymentStatus ??
    payment?.status ??
    payment?.Status ??
    null;
  return attachOrderBillingFields({
    ...order,
    orderId,
    order_id: orderId,
    displayOrderNo:
      order.displayOrderNo ??
      order.display_order_no ??
      order.Display_Order_No ??
      null,
    display_order_no:
      order.display_order_no ??
      order.displayOrderNo ??
      order.Display_Order_No ??
      null,
    orderStatus,
    orderStatusCode: code || (order.order_status_code ?? order.orderStatusCode),
    orderLineItems: lineItems,
    OrderLineItems: lineItems,
    customerName: order.customer_name ?? order.customerName ?? order.CustomerName,
    totalPrice: order.total_price ?? order.totalPrice ?? order.TotalPrice,
    createdDate: order.created_date ?? order.createdDate ?? order.CreatedDate,
    createdById:
      order.created_by_id ?? order.createdById ?? order.Created_By_Id ?? order.Created_By ?? null,
    updatedById: order.updated_by_id ?? order.updatedById ?? order.Updated_By_Id ?? null,
    updatedDate: order.updated_date ?? order.updatedDate ?? order.UpdatedDate ?? null,
    deliveryTime: order.delivery_time ?? order.deliveryTime ?? order.DeliveryTime,
    organizationName: order.organization_name ?? order.organizationName ?? order.OrganizationName,
    receiverName: order.receiver_name ?? order.receiverName ?? order.ReceiverName,
    receiverPhone: order.receiver_phone ?? order.receiverPhone ?? order.Receiver_Phone,
    orderSource: order.order_source ?? order.orderSource ?? order.Order_Source,
    orderType: order.order_type ?? order.orderType ?? order.Order_Type,
    partySize: order.partySize ?? order.party_size ?? null,
    deliveryContactName:
      order.delivery_contact_name ?? order.deliveryContactName ?? order.Delivery_Contact_Name,
    deliveryContactNumber:
      order.delivery_contact_number ??
      order.deliveryContactNumber ??
      order.Delivery_Contact_Number ??
      order.deliveryContactNo,
    paymentStatus,
    paymentMethod:
      order.payment_method ??
      order.paymentMethod ??
      order.PaymentMethod ??
      payment?.payment_method ??
      payment?.paymentMethod ??
      payment?.PaymentMethod,
    mobile: order.mobile ?? order.receiver_phone ?? order.receiverPhone ?? order.ReceiverPhone,
    payment,
    transactionId:
      order.transaction_id ??
      order.transactionId ??
      order.TransactionId ??
      payment?.transaction_id ??
      payment?.transactionId ??
      payment?.TransactionId,
    bankRRN:
      order.bank_rrn ?? order.bankRRN ?? order.BankRRN ?? payment?.bank_rrn ?? payment?.bankRRN,
    notes: order.notes ?? order.Notes,
    childId: order.child_id ?? order.childId ?? order.Child_Id,
    childName: order.child_name ?? order.childName ?? order.Child_Name,
    rollNumber: order.roll_number ?? order.rollNumber ?? order.Roll_Number,
    childClass: order.class ?? order.child_class ?? order.childClass ?? order.Class,
    section: order.section ?? order.Section,
    schoolAddress: order.school_address ?? order.schoolAddress,
    schoolAddressFormatted:
      order.school_address_formatted ??
      order.schoolAddressFormatted ??
      formatSchoolAddressFromParts(order.school_address ?? order.schoolAddress),
    subscriptionId: order.subscription_id ?? order.subscriptionId,
    billingType: order.billing_type ?? order.billingType,
    creditsConsumed: order.credits_consumed ?? order.creditsConsumed,
    restaurantSettlementAmount:
      order.restaurant_settlement_amount ?? order.restaurantSettlementAmount,
    platformFee: order.platform_fee ?? order.platformFee ?? order.Platform_Fee,
    gst: order.gst ?? order.GST ?? order.Gst ?? 0,
    deliveryFee:
      order.delivery_fee ?? order.deliveryFee ?? order.DeliveryFee ?? order.Delivery_Fee ?? 0,
    convienceFee:
      order.convience_fee ??
      order.convienceFee ??
      order.ConvenienceFee ??
      order.Convience_Fee ??
      0,
  });
}

/** Build a single-line school address from API object fields. */
export function formatSchoolAddressFromParts(addr) {
  if (!addr || typeof addr !== 'object') return null;
  const line1 = addr.address_line1 ?? addr.addressLine1 ?? '';
  const line2 = addr.address_line2 ?? addr.addressLine2 ?? '';
  const city = addr.city ?? '';
  const state = addr.state ?? '';
  const postal = addr.postal_code ?? addr.postalCode ?? '';
  const country = addr.country ?? '';
  const parts = [line1];
  if (line2) parts.push(line2);
  const cityState = [city, state].filter(Boolean).join(', ');
  const cityLine = [cityState, postal].filter(Boolean).join(' ').trim();
  if (cityLine) parts.push(cityLine);
  if (country) parts.push(country);
  const formatted = parts.filter((p) => p && String(p).trim()).join(', ');
  return formatted || null;
}

function resolveAddressMapTarget(address, formattedAddress) {
  const addr = address && typeof address === 'object' ? address : null;
  const lat = parseFloat(addr?.lat ?? addr?.Lat);
  const lon = parseFloat(addr?.lon ?? addr?.Lng ?? addr?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return { type: 'coords', lat, lon };
  }
  const query = (formattedAddress || formatSchoolAddressFromParts(addr) || '').trim();
  if (!query) return null;
  return { type: 'query', query };
}

/** Google Maps URL for an address (coordinates preferred). */
export function buildAddressMapUrl(address, formattedAddress) {
  const target = resolveAddressMapTarget(address, formattedAddress);
  if (!target) return null;
  if (target.type === 'coords') {
    return `https://www.google.com/maps?q=${target.lat},${target.lon}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(target.query)}`;
}

/** Embedded Google Maps iframe URL for an address (coordinates preferred). */
export function buildAddressMapEmbedUrl(address, formattedAddress) {
  const target = resolveAddressMapTarget(address, formattedAddress);
  if (!target) return null;
  if (target.type === 'coords') {
    return `https://www.google.com/maps?q=${target.lat},${target.lon}&z=16&output=embed`;
  }
  return `https://www.google.com/maps?q=${encodeURIComponent(target.query)}&z=16&output=embed`;
}

/** Static map preview image URL (coords preferred; address query also supported). */
export function buildAddressMapPreviewUrl(address, formattedAddress, { width = 320, height = 200 } = {}) {
  const target = resolveAddressMapTarget(address, formattedAddress);
  if (!target) return null;
  if (target.type === 'coords') {
    const { lat, lon } = target;
    return `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lon}&zoom=15&size=${width}x${height}&maptype=mapnik&markers=${lat},${lon},red-pushpin`;
  }
  // Address-only: OpenStreetMap static endpoint needs coords; UI falls back to
  // an embedded Google Maps preview iframe in the thumbnail.
  return null;
}

/** Extract YouTube video id from watch / embed / short URL. */
export function extractYoutubeVideoId(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;
  const match = value.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|v=)([A-Za-z0-9_-]{6,64})/,
  );
  if (match?.[1]) return match[1];
  try {
    const withScheme = value.startsWith('http') ? value : `https://${value}`;
    const parsed = new URL(withScheme);
    const host = (parsed.hostname || '').toLowerCase();
    if (host === 'youtu.be' || host === 'www.youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host.includes('youtube.com')) {
      const path = parsed.pathname || '';
      if (path.includes('/watch')) return parsed.searchParams.get('v');
      const embed = path.match(/^\/embed\/([^/]+)/);
      if (embed) return embed[1];
      const shorts = path.match(/^\/shorts\/([^/]+)/);
      if (shorts) return shorts[1];
      const live = path.match(/^\/live\/([^/]+)/);
      if (live) return live[1];
    }
  } catch {
    return null;
  }
  return null;
}

/** YouTube thumbnail preview URL. */
export function buildYoutubeThumbnailUrl(raw) {
  const id = extractYoutubeVideoId(raw);
  if (!id) return null;
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

/** Google Maps URL for school/org address (coordinates preferred). */
export function buildSchoolMapUrl(schoolAddress, formattedAddress) {
  return buildAddressMapUrl(schoolAddress, formattedAddress);
}

/** @deprecated Use normalizeRestaurantOrder — kept for imports. */
export function normalizeOrderHistoryOrder(order) {
  return normalizeRestaurantOrder(order, { mapHistoryStatusFromCode: true });
}

export function normalizeOrderSourceFilter(raw) {
  const s = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  if (s === 'JREATS' || s === 'JUNIOREATS') return 'JREATS';
  if (s === 'MYEATS') return 'MYEATS';
  return s;
}

export function orderMatchesSource(order, sourceFilter) {
  if (!sourceFilter) return true;
  const want = normalizeOrderSourceFilter(sourceFilter);
  const got = normalizeOrderSourceFilter(
    order?.orderSource ?? order?.order_source ?? order?.Order_Source
  );
  if (want === 'RESTAURANT' && got === 'COMPANY') return true;
  if (want === 'COMPANY' && got === 'RESTAURANT') return true;
  return got === want;
}

/** Dine-in tab: MB POS orders only; legacy local rows without source count as MB when dine-in typed. */
export function isDineInMbOrder(order, { isDineInPosOrder } = {}) {
  const src = normalizeOrderSourceFilter(
    order?.orderSource ?? order?.order_source ?? order?.Order_Source ?? ''
  );
  if (src === 'MYEATS' || src === 'JREATS') return false;
  if (src === 'MB') return true;
  if (typeof isDineInPosOrder === 'function' && isDineInPosOrder(order)) return true;
  return false;
}

function normalizeDeliveryAgentFoodItems(order) {
  const raw = order?.food_items ?? order?.FoodItems ?? order?.foodItems ?? [];
  return (Array.isArray(raw) ? raw : []).map((item) => ({
    FoodItemName:
      item.food_item_name ??
      item.item_name ??
      item.itemName ??
      item.food_name ??
      item.foodName ??
      item.FoodItemName ??
      'Item',
    Quantity: item.quantity ?? item.Quantity ?? 1,
    Price: item.price ?? item.Price,
  }));
}

export function resolveJreatsMealType(order) {
  const meal =
    order?.mealType ??
    order?.meal_type ??
    order?.Meal_Type ??
    order?.meal ??
    order?.Meal;
  if (meal != null && String(meal).trim() !== '') return String(meal).trim();
  return '';
}

/** Normalize GET /delivery-agent/:id/orders rows for courier UI (snake_case + PascalCase). */
export function normalizeDeliveryAgentOrder(order) {
  const base = normalizeRestaurantOrder(order, { mapHistoryStatusFromCode: true });
  const orderId = base.orderId ?? base.order_id;
  const foodItems = normalizeDeliveryAgentFoodItems(order);
  const restaurantId =
    order.restaurant_id ?? order.restaurantId ?? order.RestaurantId ?? order.Restaurant_Id;
  const orderKind = order.order_kind ?? order.orderKind ?? 'restaurant';
  const isStoreOrder = orderKind === 'store';
  const displayOrderRef = order.display_order_ref ?? order.displayOrderRef ?? null;

  return {
    ...base,
    orderId,
    order_id: orderId,
    OrderId: orderId,
    orderKind,
    displayOrderRef,
    display_order_ref: displayOrderRef,
    groceryOrderId: order.grocery_order_id ?? order.groceryOrderId ?? (isStoreOrder ? orderId : null),
    masterOrderId: order.master_order_id ?? order.masterOrderId ?? null,
    storeId: order.store_id ?? order.storeId ?? null,
    storeName: order.store_name ?? order.storeName ?? '',
    CustomerName: base.customerName ?? '',
    TotalPrice: base.totalPrice,
    OrderStatus: base.orderStatus,
    CreatedDate: base.createdDate,
    RestaurantId: restaurantId,
    SchoolName: isStoreOrder
      ? order.store_name ?? order.storeName ?? ''
      : order.school_name ??
        order.SchoolName ??
        base.organizationName ??
        order.organization_name ??
        '',
    FoodItems: foodItems,
    ReceiverName:
      base.deliveryContactName ??
      base.receiverName ??
      order.receiver_name ??
      order.receiverName ??
      '',
    mealType: resolveJreatsMealType(order),
    orderSource: base.orderSource,
  };
}

function isStoreOrderCompletionStatus(displayStatus) {
  const norm = String(displayStatus ?? '').toUpperCase();
  return (
    norm.includes('CMP') ||
    norm.includes('COMPLETE') ||
    norm.includes('DELIVER') ||
    norm === 'DEL'
  );
}

export async function updateDeliveryAgentOrderStatus(order, displayStatus, userId) {
  const token = localStorage.getItem('Token');
  const orderId = resolveDeliveryAgentOrderId(order);
  const statusCode = onlineDisplayStatusToOrderStatusCode(displayStatus);
  const isStoreOrder = (order?.orderKind ?? order?.order_kind) === 'store';

  if (isStoreOrder) {
    // Store grocery API resolves by Status_Code or Status label (e.g. DELIVERED).
    // Do not map to DEL here — some DBs only have DELIVERED in order_status_mt.
    await updateGroceryOrderStatus(orderId, {
      orderStatusCode: displayStatus,
      updatedBy: userId,
      ...(isStoreOrderCompletionStatus(displayStatus)
        ? { deliveredDate: new Date().toISOString() }
        : {}),
    });
    return;
  }

  await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantOrderById(orderId),
    {
      order_status_code: statusCode,
      updated_by_id: userId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}

export function getDeliveryAgentOrderSortValue(order, sortKey) {
  switch (sortKey) {
    case 'orderId':
      return str(
        order.displayOrderRef ??
          order.display_order_ref ??
          resolveDeliveryAgentOrderId(order)
      );
    case 'organization':
      return str(
        order.SchoolName ?? order.organizationName ?? order.OrganizationName
      );
    case 'customer':
      return str(
        order.CustomerName ?? order.customerName ?? order.customer_name
      );
    case 'receiver':
      return str(order.ReceiverName ?? order.receiverName);
    case 'items':
      return (order.FoodItems ?? order.food_items ?? order.foodItems ?? []).length;
    case 'total':
      return (
        Number.parseFloat(
          order.TotalPrice ?? order.totalPrice ?? order.total_price
        ) || 0
      );
    case 'status':
      return str(resolveOrderStatusRaw(order)).toUpperCase();
    case 'createdDate':
      return parseDateSortValue(
        order.CreatedDate ?? order.createdDate ?? order.created_date
      );
    default:
      return '';
  }
}

export function filterDeliveryAgentOrders(orders, { orderSource, activeOnly, historyOnly } = {}) {
  let list = (Array.isArray(orders) ? orders : []).map(normalizeDeliveryAgentOrder);

  if (orderSource) {
    list = list.filter((o) => orderMatchesSource(o, orderSource));
  }

  list = list.filter((o) => {
    const code = onlineDisplayStatusToOrderStatusCode(resolveOrderStatusRaw(o));
    if (historyOnly) return code === 'DEL' || code === 'CAN';
    if (activeOnly) return code !== 'DEL' && code !== 'CAN';
    return true;
  });

  return list.sort(
    (a, b) =>
      new Date(b.CreatedDate || b.createdDate || 0) -
      new Date(a.CreatedDate || a.createdDate || 0)
  );
}

export async function fetchDeliveryAgentOrders(userId) {
  const token = localStorage.getItem('Token');
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantDeliveryAgentOrders(userId),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const raw = Array.isArray(response.data) ? response.data : [];
    return raw.map((o) => normalizeDeliveryAgentOrder(o));
  } catch (err) {
    if (err?.response?.status === 404) return [];
    throw err;
  }
}

/**
 * GET /restaurants/:restaurantId/orders — active (non-terminal) orders for restaurant.
 */
export async function fetchRestaurantOrders(restaurantId, { orderSource, orderType } = {}) {
  const token = localStorage.getItem('Token');
  const params = {};
  if (orderSource) params.orderSource = orderSource;
  if (orderType) params.orderType = orderType;
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantOrdersByRestaurantId(restaurantId),
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        params,
      }
    );
    const raw = response.data?.orders ?? response.data?.Orders ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map((o) => normalizeRestaurantOrder(o));
  } catch (err) {
    if (err?.response?.status === 804) return [];
    throw err;
  }
}

/**
 * GET /restaurants/:restaurantId/order-history
 * Terminal statuses: CAN, DEL, CMP, PU.
 */
export async function fetchRestaurantOrderHistory(restaurantId, { orderSource, orderType } = {}) {
  const token = localStorage.getItem('Token');
  const params = {};
  if (orderSource) params.orderSource = orderSource;
  if (orderType) params.orderType = orderType;
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantOrderHistoryByRestaurantId(restaurantId),
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        params,
      }
    );
    const raw = response.data?.orders ?? response.data?.Orders ?? [];
    const list = Array.isArray(raw) ? raw : [];
    return list.map((o) => normalizeRestaurantOrder(o, { mapHistoryStatusFromCode: true }));
  } catch (err) {
    if (err?.response?.status === 804) return [];
    throw err;
  }
}

/**
 * GET /catering/order/:orderId — single catering order with line items.
 */
export async function fetchCateringOrderById(orderId) {
  if (orderId == null || orderId === '') return null;
  const token = localStorage.getItem('Token');
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.cateringOrderById(orderId),
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    return response?.data ?? null;
  } catch (err) {
    if (err?.response?.status === 404 || err?.response?.status === 804) return null;
    throw err;
  }
}

/**
 * GET /catering/orders/:restaurantId — catering orders for a restaurant.
 * Returns normalized orders (status only is needed for summaries).
 */
export async function fetchCateringActiveOrders(restaurantId) {
  if (!restaurantId) return [];
  const token = localStorage.getItem('Token');
  try {
    const response = await axiosRestaurantInstance.get(`/catering/orders/${restaurantId}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    const ordersData = response?.data?.orders ?? response?.data?.Orders ?? response?.data;
    const list = Array.isArray(ordersData) ? ordersData : [];
    return list.map((o) => normalizeRestaurantOrder(o));
  } catch (err) {
    if (err?.response?.status === 404 || err?.response?.status === 804) return [];
    throw err;
  }
}

/** Parse table number stored in order Notes (e.g. "Table: Table 3"). */
export function parseTableNoFromNotes(notes) {
  if (notes == null || notes === '') return null;
  const match = String(notes).match(/Table:\s*(.+)/i);
  return match ? match[1].trim() : null;
}

/** Resolve dine-in table from API fields or Notes. */
export function resolveOrderTableNumber(order) {
  if (!order || typeof order !== 'object') return null;
  const fromField =
    order.table_no ??
    order.tableNo ??
    order.Table_No ??
    order.tableNumber ??
    order.table_number ??
    order.TableNumber;
  if (
    fromField != null &&
    fromField !== '' &&
    String(fromField).trim() !== '' &&
    fromField !== 0 &&
    fromField !== '0'
  ) {
    return String(fromField).trim();
  }
  return parseTableNoFromNotes(order.notes ?? order.Notes);
}

/** Map restaurant order row to dine-in / POS UI shape (replaces legacy offline order API). */
export function mapPosOrderFromApi(order) {
  const normalized = normalizeRestaurantOrder(order);
  const lineItems = (normalized.orderLineItems || []).map((li) => ({
    foodItemName: li.foodItemName,
    foodQuantity: li.foodQuantity,
    price: li.price,
    orderLineId: li.orderLineId,
    offlineLineItemId: li.orderLineId,
    dailyItemId: li.dailyItemId ?? li.Daily_Item_Id ?? null,
    foodItemId: li.dailyItemId ?? li.Daily_Item_Id ?? li.foodItemId ?? null,
  }));
  const tableNumber = resolveOrderTableNumber({ ...order, ...normalized });
  const orderId = normalized.orderId;
  return {
    ...normalized,
    orderId,
    offlineOrderId: orderId,
    customerName: normalized.deliveryContactName ?? normalized.customerName,
    customerPhone: normalized.deliveryContactNumber,
    lineItems,
    orderLineItems: lineItems,
    tableNumber,
    table_number: tableNumber,
  };
}

/** GET /restaurant/orders/:orderId — single order with line items and payment. */
export async function fetchOrderById(orderId) {
  if (orderId == null || orderId === '') return null;
  const token = localStorage.getItem('Token');
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantOrderById(orderId),
      {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    const raw = response.data?.order ?? response.data;
    if (!raw || (raw.order_id == null && raw.orderId == null && raw.Order_Id == null)) {
      return null;
    }
    return normalizeRestaurantOrder(raw);
  } catch (err) {
    if (err?.response?.status === 404 || err?.response?.status === 804) return null;
    throw err;
  }
}

/** Find one POS order by id from active + history lists. */
export async function fetchRestaurantOrderById(restaurantId, orderId) {
  if (!restaurantId || orderId == null) return null;
  const opts = { orderSource: 'MB' };
  const [active, history] = await Promise.all([
    fetchRestaurantOrders(restaurantId, opts).catch(() => []),
    fetchRestaurantOrderHistory(restaurantId, opts).catch(() => []),
  ]);
  const found = [...active, ...history].find(
    (o) => Number(o.orderId) === Number(orderId)
  );
  return found ? mapPosOrderFromApi(found) : null;
}

/** Active + history POS orders with payment info for dine-in order tables. */
export async function fetchPosOrdersWithPayments(restaurantId) {
  const opts = { orderSource: 'MB' };
  const [active, history, paymentsData] = await Promise.all([
    fetchRestaurantOrders(restaurantId, opts).catch(() => []),
    fetchRestaurantOrderHistory(restaurantId, opts).catch(() => []),
    fetchRestaurantPayments(restaurantId).catch(() => ({})),
  ]);
  const seen = new Set();
  const mergedApiOrders = [];
  for (const order of [...active, ...history]) {
    const id = resolveOrderId(order);
    if (id == null || id === '' || seen.has(String(id))) continue;
    if (!orderMatchesSource(order, 'MB')) continue;
    seen.add(String(id));
    mergedApiOrders.push(order);
  }
  const paymentsList = paymentsData?.payments ?? paymentsData?.Payments ?? [];
  const paymentByOrderId = new Map();
  paymentsList.forEach((p) => {
    const oid = p.orderId ?? p.order_id ?? p.Order_Id;
    if (oid != null) paymentByOrderId.set(Number(oid), p);
  });

  return mergedApiOrders.map((order) => {
    const normalized = normalizeRestaurantOrder(order);
    const paymentFromList = paymentByOrderId.get(Number(normalized.orderId));
    const nestedPayment = normalized.payment;
    const payment = paymentFromList || nestedPayment;
    const paymentStatus =
      paymentFromList?.status ??
      paymentFromList?.Status ??
      normalized.paymentStatus ??
      nestedPayment?.status ??
      nestedPayment?.Status ??
      null;
    const paymentMethod =
      paymentFromList?.paymentMethod ??
      paymentFromList?.payment_method ??
      paymentFromList?.PaymentMethod ??
      normalized.paymentMethod ??
      nestedPayment?.payment_method ??
      nestedPayment?.paymentMethod ??
      nestedPayment?.PaymentMethod ??
      null;
    const lineItems = normalized.orderLineItems || [];
    const items =
      lineItems.length > 0
        ? lineItems.map((li) => ({
            foodItemId: li.dailyItemId ?? li.foodItemId ?? null,
            dailyItemId: li.dailyItemId ?? null,
            foodItemName: li.foodItemName ?? 'Item',
            foodQuantity: li.foodQuantity ?? 1,
            price: parseFloat(li.price || 0),
            orderLineId: li.orderLineId,
            offlineLineItemId: li.orderLineId,
          }))
        : [];

    return {
      orderId: normalized.orderId,
      orderNumber: normalized.orderId,
      displayOrderNo: normalized.displayOrderNo ?? normalized.display_order_no ?? null,
      customerName: normalized.deliveryContactName ?? normalized.customerName,
      customerMobile: normalized.deliveryContactNumber,
      mobile: normalized.deliveryContactNumber,
      paymentStatus,
      paymentMethod,
      payment: payment || null,
      createdById: normalized.createdById ?? null,
      created_by_id: normalized.createdById ?? null,
      totalPrice: parseFloat(normalized.totalPrice || 0),
      orderStatus: String(
        normalized.orderStatus ||
          ORDER_STATUS_FROM_CODE[String(normalized.orderStatusCode || '').toUpperCase()] ||
          'NEW'
      ).toUpperCase(),
      orderStatusCode: normalized.orderStatusCode || null,
      createdDate: normalized.createdDate,
      restaurantId: normalized.restaurantId ?? restaurantId,
      billNo: normalized.displayOrderNo ?? normalized.display_order_no ?? normalized.orderId,
      tableNumber: resolveOrderTableNumber({ ...order, ...normalized }),
      orderType: normalized.orderType,
      orderSource: normalized.orderSource ?? 'MB',
      OrderLineItems: items,
      orderLineItems: items,
    };
  });
}

/**
 * PUT /restaurant/dine-in/orders/:orderId — add line items and/or update DINE-IN status.
 */
export async function updateDineInOrder(orderId, payload = {}) {
  const token = localStorage.getItem('Token');
  const body = {};
  if (payload.lineItems != null) body.lineItems = payload.lineItems;
  if (payload.orderStatus != null) body.orderStatus = payload.orderStatus;
  if (payload.orderStatusCode != null) body.orderStatusCode = payload.orderStatusCode;
  if (payload.tableNo != null) body.tableNo = payload.tableNo;
  if (payload.updatedById != null) body.updatedById = payload.updatedById;

  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantDineInOrderById(orderId),
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  return response.data ?? {};
}

/**
 * PUT /restaurants/orders/:orderId — update status / contact (no line-item edits).
 */
export async function updateRestaurantOrder(orderId, payload = {}) {
  const token = localStorage.getItem('Token');
  const body = {};
  if (payload.orderStatus != null) body.order_status = payload.orderStatus;
  if (payload.orderStatusCode != null) body.order_status_code = payload.orderStatusCode;
  if (payload.deliveryContactName != null) {
    body.delivery_contact_name = payload.deliveryContactName;
  }
  if (payload.customerName != null) {
    body.delivery_contact_name = payload.customerName;
  }
  if (payload.deliveryContactNumber != null) {
    body.delivery_contact_number = payload.deliveryContactNumber;
  }
  if (payload.customerPhone != null) {
    body.delivery_contact_number = payload.customerPhone;
  }
  if (payload.orderType != null) body.order_type = payload.orderType;
  if (payload.orderSource != null) body.order_source = payload.orderSource;
  if (payload.updatedById != null) body.updated_by_id = payload.updatedById;

  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantOrderById(orderId),
    body,
    {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return response.data ?? {};
}

/** PUT /restaurants/cancel/:orderId */
export async function cancelRestaurantOrder(orderId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantCancelOrder(orderId),
    {},
    {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  return response.data ?? {};
}

/** PUT /catering/cancel-order/:orderId */
export async function cancelCateringOrder(orderId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.cateringCancelOrder(orderId),
    {},
    {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  return response.data ?? {};
}

/**
 * GET /restaurants/:restaurantId/payments — online (and shared) payment summary.
 * Response: date keys { amount, count } plus optional payments[].
 */
export async function fetchRestaurantPayments(restaurantId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantPaymentsByRestaurantId(restaurantId),
    {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  return response.data ?? {};
}

/**
 * GET /catering/payments/:restaurantId — catering payment summary by restaurant.
 * Response: date keys { amount, count }, items[], payments[].
 */
export async function fetchCateringPayments(restaurantId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.cateringPaymentsByRestaurantId(restaurantId),
    {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  return response.data ?? {};
}

/** Build daily summary rows from payments API body. */
export function normalizeRestaurantPaymentsSummary(data) {
  const paymentsList = data?.payments ?? data?.Payments ?? [];
  const byDate = {};
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const skipKeys = new Set(['payments', 'Payments', 'items', 'Items']);

  Object.entries(data || {}).forEach(([key, val]) => {
    if (skipKeys.has(key)) return;
    if (dateRegex.test(key) && val && typeof val === 'object') {
      const count = val.count ?? 0;
      const amount = parseFloat(val.amount || 0);
      byDate[key] = { date: key, totalOrders: count, totalAmount: amount };
    }
  });

  if (Object.keys(byDate).length === 0 && Array.isArray(paymentsList) && paymentsList.length > 0) {
    paymentsList.forEach((p) => {
      const dateStr = (p.createdDate || p.createdAt || '').toString().trim();
      const date = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr.substring(0, 10);
      if (!date) return;
      if (!byDate[date]) {
        byDate[date] = { date, totalOrders: 0, totalAmount: 0 };
      }
      byDate[date].totalOrders += 1;
      byDate[date].totalAmount += parseFloat(p.amount || p.finalAmount || 0);
    });
  }

  return Object.values(byDate)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .map((row) => ({
      ...row,
      totalAmount: Number(row.totalAmount).toFixed(2),
    }));
}

export const POS_ORDER_TYPES = ['DINE-IN', 'PARCEL'];

/** Display label for POS order type dropdown. */
export function getPosOrderTypeLabel(value) {
  const normalized = normalizePosOrderType(value);
  if (normalized === 'DINE-IN') return 'Dine-In';
  if (normalized === 'PARCEL') return 'To Go/ Parcel';
  return 'Dine-in';
}

/** Map UI / API values to POST /restaurants/orders orderType. */
export function normalizePosOrderType(value) {
  const raw = String(value ?? 'DINE-IN')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '-');
  if (raw === 'TO-GO' || raw === 'TOGO' || raw === 'PARCEL') return 'PARCEL';
  if (raw === 'DINE-IN' || raw === 'DINEIN') return 'DINE-IN';
  return 'DINE-IN';
}

/** Format table number for dine-in orders (e.g. "1" → "Table 1"). */
export function formatPosTableNo(tableNumber) {
  if (tableNumber == null || String(tableNumber).trim() === '') return '';
  const s = String(tableNumber).trim();
  return /^table\s/i.test(s) ? s : `Table ${s}`;
}

/** Table column display — number only, strips a leading "Table" prefix. */
export function formatTableNumberDisplay(tableNumber) {
  if (tableNumber == null || String(tableNumber).trim() === '') return 'N/A';
  const s = String(tableNumber).trim();
  const digits = s.replace(/^table\s*#?\s*/i, '').trim();
  return digits || 'N/A';
}

/** Build line items for POST /restaurants/orders (POS). */
export function buildPosOrderLineItems(cart) {
  return (cart || [])
    .map((item) => {
      const dailyItemId = Number(
        item.dailyItemId ??
          item.Daily_Item_Id ??
          item.DailyItemId ??
          item.foodItemId ??
          item.id ??
          0
      );
      const qty =
        typeof item.quantity === 'number'
          ? item.quantity
          : parseInt(item.quantity, 10) || 1;
      const price = parseFloat(item.price || 0);
      if (!dailyItemId || dailyItemId <= 0 || !Number.isFinite(price)) return null;
      return {
        dailyItemId,
        foodItemName: item.foodItemName || item.name || 'Item',
        foodQuantity: qty,
        price,
      };
    })
    .filter(Boolean);
}

/**
 * POST /restaurants/orders — create dine-in / POS order.
 * Response: { message, orderId, orderLineItemIds[], totalPrice }
 */
export async function createRestaurantOrder(payload) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsOrders, payload, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const data = response.data ?? {};
  const orderId = data.orderId ?? data.OrderId ?? data.order_id;
  const displayOrderNo =
    data.displayOrderNo ?? data.display_order_no ?? data.Display_Order_No ?? null;
  return {
    ...data,
    orderId,
    displayOrderNo,
    display_order_no: displayOrderNo,
    billNo: displayOrderNo ?? data.billNo ?? data.bill_no ?? null,
    orderLineItemIds:
      data.orderLineItemIds ??
      data.OrderLineItemIds ??
      data.order_line_item_ids ??
      [],
    totalPrice: data.totalPrice ?? data.TotalPrice ?? data.total_price,
    message: data.message ?? data.Message,
  };
}

/** Map POS payment dropdown value to API paymentMethod (e.g. gpay → UPI). */
export function normalizeRestaurantPaymentMethod(method) {
  const m = String(method || 'CASH')
    .trim()
    .toUpperCase();
  if (['GPAY', 'PAYTM', 'PHONEPE', 'UPI'].includes(m)) return 'UPI';
  if (m === 'CARD') return 'CARD';
  if (m === 'CASH') return 'CASH';
  return m || 'CASH';
}

/**
 * POST /restaurants/payments — record payment after order is created.
 * Response: { message, orderId, paymentId }
 */
export async function createRestaurantPayment({
  orderId,
  amount,
  bankRRN = '',
  transactionId,
  paymentMethod = 'CASH',
  customerDetails = '',
  status = 'SUCCESS',
  createdById = 1,
}) {
  const token = localStorage.getItem('Token');
  const method = normalizeRestaurantPaymentMethod(paymentMethod);
  const isCash = method === 'CASH';
  const txn =
    transactionId != null && String(transactionId).trim()
      ? String(transactionId).trim()
      : isCash
        ? ''
        : `TXN${Date.now()}`;
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantsPayments,
    {
      orderId: Number(orderId),
      amount: Number(amount),
      bankRRN: isCash ? '' : bankRRN ?? '',
      transactionId: txn,
      paymentMethod: method,
      customerDetails: customerDetails ?? '',
      status: status ?? 'SUCCESS',
      createdById: Number(createdById),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  const data = response.data ?? {};
  return {
    ...data,
    orderId: data.orderId ?? data.OrderId ?? orderId,
    paymentId: data.paymentId ?? data.PaymentId,
    message: data.message ?? data.Message,
  };
}
